/**
 * CallbackProcessing Hook
 * 
 * Provides utilities for handling OAuth callbacks and connection state management across providers.
 * This implementation uses the wealthica2 storage and Edge Functions for Wealthica connections.
 */
import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/authStore';
import { logger } from '@/services/LoggingService';
import { WealthicaStorage } from '@/providers/wealthica2/utils/storage';
import { supabase } from '@/integrations/supabase/client';
import { ensureValidToken } from '@/services/AuthAwareApiService';

/**
 * Hook for processing OAuth callbacks across different investment providers
 * 
 * Provides utilities for storing state parameters, processing connections,
 * and handling redirects after OAuth flows complete.
 * 
 * @returns {Object} Callback processing utilities and state
 */
export function useCallbackProcessing() {
  const [processingCallback, setProcessingCallback] = useState(false);
  const [callbackDetails, setCallbackDetails] = useState<{
    provider: string | null;
    connectionId: string | null;
    error: string | null;
  } | null>(null);
  
  // No need to extract connection methods as we'll use wealthica2Client directly
  const { user } = useAuthStore();
  
  /**
   * Stores Wealthica state parameter for CSRF protection during OAuth flow
   * 
   * @param {string} state - The state parameter to store
   * @returns {boolean} Whether the operation was successful
   */
  const storeWealthicaState = useCallback((state: string) => {
    if (!state) {
      logger.warn('CallbackProcessing', 'Attempted to store empty state');
      return false;
    }
    
    try {
      logger.debug('CallbackProcessing', 'Storing Wealthica state', {
        stateLength: state?.length || 0
      });
      
      // Store as the connection state parameter for CSRF validation
      // Note: There's no direct setAuthState, so we create a minimal connection object
      const connectionData = {
        institutionId: 'temp', // This will be replaced when the real connection is established
        institutionIdentifier: 'temp', // This will be replaced when the real connection is established
        providerName: 'wealthica', // Default for regular connections, will be updated to 'demo' for demo connections in callback
        timestamp: Date.now(),
        state: state
      };
      
      WealthicaStorage.setConnectionData(connectionData);
      return true;
    } catch (error) {
      logger.error('CallbackProcessing', 'Error storing Wealthica state', {
        error: error instanceof Error ? error.message : String(error)
      });
      return false;
    }
  }, []);
  
  /**
   * Retrieves the stored Wealthica state parameter
   * 
   * @returns {string | null} The stored state or null if not found
   */
  const getWealthicaState = useCallback(() => {
    try {
      // Try to retrieve state from the connection data
      const connectionData = WealthicaStorage.getConnectionData();
      const state = connectionData?.state;
      
      logger.debug('CallbackProcessing', 'Retrieved Wealthica state', {
        hasState: !!state,
        stateLength: state?.length,
        connectionData: !!connectionData
      });
      
      return state;
    } catch (error) {
      logger.error('CallbackProcessing', 'Error retrieving Wealthica state', {
        error: error instanceof Error ? error.message : String(error)
      });
      return null;
    }
  }, []);
  
  /**
   * Process a Wealthica connection by saving it via the wealthica2-save-connection Edge Function
   * 
   * @param {string} institution_id - The Wealthica institution ID
   * @param {string} [provider_name] - Optional provider name (defaults to 'demo')
   * @returns {Promise<boolean>} Whether the connection was successfully processed
   */
  const processWealthicaConnection = useCallback(async (institution_id: string, provider_name?: string) => {
    if (!user || !institution_id) {
      logger.info('Wealthica', 'Connection missing required data', { 
        hasUser: !!user, 
        hasInstitutionId: !!institution_id 
      });
      return false;
    }
    
    try {
      // Ensure valid token before making API call
      await ensureValidToken('wealthica2-save-connection');
      
      // Call the wealthica2-save-connection Edge Function directly
      // Since wealthica2Client doesn't have a saveConnection method
      const { data, error } = await supabase.functions.invoke('wealthica2-save-connection', {
        body: {
          user_id: user.id,
          institution_id,
          provider_name: provider_name || 'demo',
          login_name: user.id // Use the user's ID as the login_name for consistency
        }
      });
      
      if (error || !data?.success) {
        logger.error('CallbackProcessing', 'Error saving connection', {
          error: error?.message || (data?.error || 'Unknown error')
        });
        return false;
      }
      
      return true;
    } catch (error) {
      logger.error('CallbackProcessing', 'Error processing Wealthica connection', {
        error: error instanceof Error ? error.message : String(error),
        institution_id,
        provider_name
      });
      return false;
    }
  }, [user]);
  
  /**
   * Cleans up all Wealthica storage when connection flow completes or errors
   * 
   * @returns {boolean} Whether cleanup was successful
   */
  const cleanupWealthicaState = useCallback(() => {
    logger.debug('CallbackProcessing', 'Cleaning up Wealthica state');
    
    try {
      // Use safe logout clearing to preserve user authentication
      WealthicaStorage.clearForLogout();
      return true;
    } catch (error) {
      logger.error('CallbackProcessing', 'Error cleaning up Wealthica state', {
        error: error instanceof Error ? error.message : String(error)
      });
      return false;
    }
  }, []);
  
  /**
   * Handles redirecting the user after connection processing completes
   * 
   * @param {string} [path='/investments/connect'] - The path to redirect to
   * @param {number} [delayMs=0] - Optional delay before redirecting (in milliseconds)
   */
  const handleRedirect = useCallback((path: string = '/investments/connect', delayMs: number = 0) => {
    logger.debug('CallbackProcessing', 'Handling redirect', {
      path,
      delayMs
    });
    
    try {
      if (delayMs > 0) {
        setTimeout(() => {
          try {
            cleanupWealthicaState();
            setProcessingCallback(false);
            window.location.href = path;
          } catch (error) {
            logger.error('CallbackProcessing', 'Error during delayed redirect', {
              error: error instanceof Error ? error.message : String(error)
            });
            window.location.href = path;
          }
        }, delayMs);
      } else {
        cleanupWealthicaState();
        setProcessingCallback(false);
        window.location.href = path;
      }
    } catch (error) {
      logger.error('CallbackProcessing', 'Error handling redirect', {
        error: error instanceof Error ? error.message : String(error)
      });
      window.location.href = path;
    }
  }, [cleanupWealthicaState]);
  
  /**
   * Handles completion of the connection flow
   * Cleans up storage and resets state
   */
  const handleComplete = useCallback(() => {
    logger.debug('CallbackProcessing', 'Handling completion');
    
    try {
      setProcessingCallback(false);
      setCallbackDetails(null);
      
      cleanupWealthicaState();
    } catch (error) {
      logger.error('CallbackProcessing', 'Error during completion', {
        error: error instanceof Error ? error.message : String(error)
      });
    }
  }, [cleanupWealthicaState]);
  
  return {
    processingCallback,
    callbackDetails,
    setProcessingCallback,
    setCallbackDetails,
    handleComplete,
    processConnection: processWealthicaConnection,
    storeWealthicaState,
    getWealthicaState,
    cleanupWealthicaState,
    handleRedirect
  };
}
